import { ReactNode, useCallback, useMemo, useState } from 'react';
import { components } from '@api/club/generated';

import { cx } from '@libs/classnames';
import { CollapseIcon } from '@ui/components/CollapseIcon';
import { GameResultsBadge } from '@ui/components/GameResultBadge';
import { HorizontalScroll } from '@ui/components/HorizontalScroll';
import { Table, TableCell, TableRow } from '@ui/components/Table';

import {
  ResultsTableParticipant,
  type TournamentSystem,
  TournamentAdditionalScore,
  ResultsGame,
  CellResult,
} from '@features/shared/api/typings';

import styles from './ResultsTable.module.css';

const additionalScoreMap: Record<TournamentAdditionalScore, string> = {
  Berger: 'КБ',
  PartBerger: 'УБ',
  Match: 'ЛВ',
  ScoreSum: 'СО',
  Black: 'РЧ',
  Buchholz: 'КБ',
  TruncatedBuchholz: 'УБ',
  IndividualBuchholzSum: 'СБ',
  IndividualPlaceSum: 'СМ',
};

export type Props = {
  className?: string;
  rows: ResultsTableParticipant[];
  rounds: {
    roundNumber: number;
    type: components['schemas']['RoundType'];
    status: components['schemas']['RoundStatus'];
  }[];
  system: TournamentSystem;
  eventFinished: boolean;
  additionalScoresOrder: TournamentAdditionalScore[];
  isRcf?: boolean;
  onGameClick?: (gameId: string) => void;
  getGameLink?: (gameId: string) => string;
  renderShieldQuickPeek?: (userId: string) => ReactNode;
  onGameMouseEnter?: (gameId: string, el: HTMLElement) => void;
  onGameMouseLeave?: () => void;
  onRowMouseLeave?: () => void;
};
export function ResultsTable({
  className,
  rows,
  rounds,
  system,
  eventFinished: isFinished,
  additionalScoresOrder,
  isRcf,
  getGameLink,
  onGameClick,
  renderShieldQuickPeek,
  onGameMouseEnter,
  onGameMouseLeave,
  onRowMouseLeave,
}: Props) {
  const [visibleGroups, setVisibleGroups] = useState<number[]>([]);
  const isMatch = system === 'OneOnOne';

  const onToggle = useCallback(
    (num: number) => {
      if (visibleGroups.includes(num)) {
        const newArr = [...visibleGroups];
        newArr.splice(visibleGroups.indexOf(num), 1);
        setVisibleGroups(newArr);
      } else {
        setVisibleGroups([num, ...visibleGroups]);
      }
    },
    [visibleGroups, setVisibleGroups],
  );

  const gamesCount = rows[0]?.games.length;

  if (!['RoundRobin', 'OneOnOne', 'Swiss'].includes(system) || !rows[0] || !gamesCount) {
    return null;
  }

  const roundNames: string[] = [];
  if (system === 'OneOnOne') {
    let i = 1;
    let currentType = '';
    rounds.forEach((r) => {
      if (r.type !== currentType) {
        i = 1;
        currentType = r.type;
      }

      if (r.type === 'Normal') {
        roundNames.push('' + i);
      } else if (r.type === 'TieBreak') {
        roundNames.push(`Доп. ${i}`);
      } else if (r.type === 'Armageddon') {
        roundNames.push('Армагеддон');
      }

      i++;
    });
  } else {
    rows[0].games.forEach((_, i) => {
      roundNames.push((i + 1).toString());
    });
  }

  const filteredAdditionalScores = additionalScoresOrder.filter((aS) => aS !== 'Match');

  const showElo = system === 'Swiss';

  return (
    <HorizontalScroll className={cx(className, styles.root)} enableForDesktop>
      <Table className={styles.table}>
        <TableRow header>
          <TableCell colSpan={2} />
          <TableCell>Имя</TableCell>

          {showElo && (
            <TableCell right rightGap>
              {isRcf ? <span className={styles.rcfHeaderText}>Рейтинг ФШР</span> : 'Рейтинг'}
            </TableCell>
          )}

          {roundNames.map((_, i) => (
            <TableCell key={roundNames[i] || ++i}>{roundNames[i] || ++i}</TableCell>
          ))}

          {filteredAdditionalScores.map((s) => (
            <TableCell key={s} right className={styles.scoreHeader}>
              {additionalScoreMap[s]}
            </TableCell>
          ))}

          <TableCell right className={styles.scoreHeader}>
            Очки
          </TableCell>
          {!isMatch && (
            <TableCell right className={styles.placeHeader}>
              Место
            </TableCell>
          )}
        </TableRow>

        {rows.map((participant, i) => (
          <ResultsRow
            key={participant.title + i}
            participant={participant}
            system={system}
            matchFinished={isFinished}
            onToggle={onToggle}
            isToggled={visibleGroups.includes(participant.no)}
            additionalScoresOrder={filteredAdditionalScores}
            onGameClick={onGameClick}
            getGameLink={getGameLink}
            showElo={showElo}
            renderShieldQuickPeek={renderShieldQuickPeek}
            onGameMouseEnter={onGameMouseEnter}
            onGameMouseLeave={onGameMouseLeave}
            onRowMouseLeave={onRowMouseLeave}
          />
        ))}
      </Table>
    </HorizontalScroll>
  );
}

function ResultsRow({
  participant,
  system,
  isChild,
  isParentBanned,
  onToggle,
  isToggled,
  matchFinished,
  additionalScoresOrder,
  getGameLink,
  showElo,
  renderShieldQuickPeek,
  onGameClick,
  onGameMouseEnter,
  onGameMouseLeave,
  onRowMouseLeave,
}: {
  participant: ResultsTableParticipant;
  system: TournamentSystem;
  matchFinished: boolean;
  isChild?: boolean;
  isParentBanned?: boolean;
  onToggle?: (num: number) => void;
  isToggled?: boolean;
  additionalScoresOrder: Props['additionalScoresOrder'];
  getGameLink?: Props['getGameLink'];
  showElo: boolean;
  renderShieldQuickPeek?: Props['renderShieldQuickPeek'];
  onGameClick?: Props['onGameClick'];
  onGameMouseEnter?: Props['onGameMouseEnter'];
  onGameMouseLeave?: Props['onGameMouseLeave'];
  onRowMouseLeave?: Props['onRowMouseLeave'];
}) {
  const isMatch = system === 'OneOnOne';
  const isSwiss = system === 'Swiss';
  const isGroup = !!participant.subRows?.length;
  const onClick = useCallback(() => {
    onToggle?.(participant.no);
  }, [onToggle, participant]);

  const place: string =
    participant?.place && matchFinished
      ? (['1', '2', '3'].includes(participant.place.toString()) &&
          'I'.repeat(Number(participant.place))) ||
        participant.place
      : '';

  const isBanned = useMemo(
    () => participant.games.some((g) => g.resultType === 'Banned'),
    [participant],
  );

  const isTwoReplays = Boolean(participant.games2);

  const row = (
    <TableRow
      className={cx(styles.large, {
        [styles.double]: isTwoReplays,
        [styles.subRow]: isChild,
        [styles.absent]: participant.isAbsent,
        [styles.banned]: isBanned,
      })}
      group={isGroup}
      hoverable
      onMouseLeave={onRowMouseLeave}
    >
      <TableCell className={cx(styles.numbers, styles.left, styles.tableNumber)}>
        <span>{!isChild && participant.no}</span>
      </TableCell>
      <TableCell className={cx(styles.numbers, styles.left, styles.seedNumber)}>
        <span>{isSwiss && !isChild && participant.seedNumber}</span>
      </TableCell>
      <TableCell onClick={(isGroup && onClick) || undefined}>
        <div className={styles.titleContainer}>
          <div
            className={cx(styles.titleContent, {
              [styles.is_group_title]: isGroup,
            })}
            title={participant.title}
          >
            <div className={styles.title}>
              {isChild && <span className={styles.childNo}>{participant.no}</span>}{' '}
              {participant.title}
            </div>
            {!isGroup && participant.partyId ? renderShieldQuickPeek?.(participant.partyId) : null}
          </div>
          {isGroup && <CollapseIcon className={styles.collapseIcon} toggled={isToggled} />}
        </div>
      </TableCell>

      {showElo && (
        <TableCell right rightGap>
          {participant.elo}
        </TableCell>
      )}

      {participant.games.map((game, i) => {
        const game2 = participant.games2?.[i];

        const isG1Bye = isBye(game);
        const isG2Bye = isBye(game2);

        return (
          <TableCell key={game.gameId + '' + i} className={styles.result}>
            <div className={styles.resultContent}>
              <GameResultsBadge
                gameId={game.gameId}
                gameLink={game.gameId ? getGameLink?.(game.gameId) : undefined}
                additional={
                  isG1Bye ? 'Bye' : isSwiss && !isChild ? game.opponentSeedNumber : undefined
                }
                large
                fullWidth={isTwoReplays}
                side={game.side}
                cellResult={game.cellResult}
                result={game.resultType}
                score={game.score}
                onGameClick={onGameClick}
                onMouseEnter={onGameMouseEnter}
                onMouseLeave={onGameMouseLeave}
              />
              {game2 && game2.resultType !== 'Skipped' && (
                <GameResultsBadge
                  gameId={game2.gameId}
                  gameLink={game2.gameId ? getGameLink?.(game2.gameId) : undefined}
                  additional={
                    isG2Bye ? 'Bye' : isSwiss && !isChild ? game2.opponentSeedNumber : undefined
                  }
                  fullWidth
                  large
                  side={game2.side}
                  cellResult={game2.cellResult}
                  result={game2.resultType}
                  score={game2.score}
                  onGameClick={onGameClick}
                  onMouseEnter={onGameMouseEnter}
                  onMouseLeave={onGameMouseLeave}
                />
              )}
            </div>
          </TableCell>
        );
      })}

      {additionalScoresOrder.map((as) => (
        <TableCell key={as} className={styles.score} right>
          <span>
            {
              participant.additionalScores?.find((s) => s?.scoreType === as || s?.type === as)
                ?.score
            }
          </span>
        </TableCell>
      ))}

      <TableCell className={styles.totalScore} right>
        <span>{(!isChild || isMatch) && participant.totalScore}</span>
      </TableCell>
      {!isMatch && (
        <TableCell className={styles.place} right>
          {!isChild && !isBanned && (
            <span
              className={cx(styles.placeContent, {
                [styles.winningPlace]: matchFinished && ['I', 'II', 'III'].includes(place),
              })}
            >
              {place}
            </span>
          )}
          {isBanned && (
            <div
              className={cx(styles.banBadge, {
                [styles.banBadgeDimmed]: isParentBanned,
              })}
            >
              <div>Бан</div>
            </div>
          )}
        </TableCell>
      )}
    </TableRow>
  );

  return isChild ? (
    row
  ) : (
    <>
      {row}
      {isToggled &&
        participant.subRows?.map((sub, j) => (
          <ResultsRow
            key={sub.title + j}
            participant={sub}
            system={system}
            matchFinished={matchFinished}
            isChild
            isParentBanned={isBanned}
            additionalScoresOrder={additionalScoresOrder}
            onGameClick={onGameClick}
            getGameLink={getGameLink}
            showElo={showElo}
            renderShieldQuickPeek={renderShieldQuickPeek}
            onGameMouseEnter={onGameMouseEnter}
            onGameMouseLeave={onGameMouseLeave}
          />
        ))}
    </>
  );
}

const BYE_RESULTS: CellResult[] = [
  'UnpairedBye', // — настоящий BYE
  // 'ZeroPointBye' — игрок не играл т.к. удалён
  // 'BanBye', // — настоящий BYE, но потом игрока забанили о поменяли результат с 1 на 0
];

function isBye(game?: ResultsGame) {
  return (
    (game?.cellResult && BYE_RESULTS.includes(game.cellResult)) ||
    game?.resultType === 'ByeIn' ||
    (game?.resultType === 'UnPlayed' && game.score === 1 && game.side === 'Unknown')
  );
}
